import { useState, useEffect } from "react";
import { UtensilsCrossed, RefreshCw, UserCheck, Clock, Calendar } from "lucide-react";
import toast from "react-hot-toast";
import { getLunchUpdates } from "@/services/admin";

interface LunchUpdate {
  id: number;
  Date: string;
  Status: string;
  Created_At: string;
  Username: string;
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function LunchUpdatesView() {
  const [updates, setUpdates] = useState<LunchUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigningId, setAssigningId] = useState<number | null>(null);

  const loadUpdates = async () => {
    setLoading(true);
    try {
      const res = await getLunchUpdates();
      if (res.status === 200 && res.data) {
        const data = res.data as any;
        setUpdates(Array.isArray(data) ? data : data?.results ?? []);
      }
    } catch {
      // toast.error("Failed to load lunch updates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUpdates();
  }, []);

  const handleAssign = async (update: LunchUpdate) => {
    setAssigningId(update.id);
    try {
      await new Promise((r) => setTimeout(r, 800));
      toast.success(`Assigned for ${update.Username}`);
    } catch {
      toast.error("Failed to assign");
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
            <UtensilsCrossed className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lunch Prepared Updates</h1>
            <p className="text-sm text-gray-500 mt-0.5">Parents who have marked lunch as ready</p>
          </div>
        </div>
        <button
          onClick={loadUpdates}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Loading updates…</p>
          </div>
        ) : updates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center">
              <UtensilsCrossed className="w-10 h-10 text-orange-300" />
            </div>
            <p className="text-base font-semibold text-gray-700">No updates yet</p>
            <p className="text-sm text-gray-400">No lunch prepared notifications today</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
              <tr>
                <th className="text-left py-3.5 px-5 text-xs font-semibold text-gray-600 uppercase tracking-wide">Parent</th>
                <th className="text-left py-3.5 px-5 text-xs font-semibold text-gray-600 uppercase tracking-wide">Date</th>
                <th className="text-left py-3.5 px-5 text-xs font-semibold text-gray-600 uppercase tracking-wide">Notified At</th>
                <th className="text-left py-3.5 px-5 text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                <th className="text-left py-3.5 px-5 text-xs font-semibold text-gray-600 uppercase tracking-wide">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {updates.map((update) => (
                <tr key={update.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm">
                        {update.Username?.[0]?.toUpperCase() ?? "U"}
                      </div>
                      <span className="font-semibold text-sm text-gray-900">{update.Username}</span>
                    </div>
                  </td>
                  <td className="py-4 px-5">
                    <span className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      {formatDate(update.Date)}
                    </span>
                  </td>
                  <td className="py-4 px-5">
                    <span className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      {formatDateTime(update.Created_At)}
                    </span>
                  </td>
                  <td className="py-4 px-5">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
                      {update.Status}
                    </span>
                  </td>
                  <td className="py-4 px-5">
                    <button
                      onClick={() => handleAssign(update)}
                      disabled={assigningId === update.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      {assigningId === update.id ? "Assigning…" : "Assign / Reassign"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Footer */}
        {!loading && updates.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-400">{updates.length} update{updates.length !== 1 ? "s" : ""} found</p>
          </div>
        )}
      </div>
    </div>
  );
}
